/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { removeDuplicateStrings } from '../removeDuplicateStrings/removeDuplicateStrings';
import type { getDependantFeaturesArgs } from './types';

export function getDependantFeatures( { feature, featurePlugins, capabilities }: getDependantFeaturesArgs ): Array<string> {
	const validDependantFeatures = capabilities
		.flatMap( capability => capability.features );

	const requirements = featurePlugins
		// Getting all required plugins.
		.flatMap( plugin => plugin.requires )
		// Mapping all required plugins to their required features.
		.map( requirement => {
			function pluginNameToFeatureName( pluginName: string ): string | undefined {
				return validDependantFeatures.find( feature => feature.plugins.includes( pluginName ) )?.name;
			}

			if ( typeof requirement === 'string' ) {
				return pluginNameToFeatureName( requirement );
			}

			return requirement.map( requiredPluginName => pluginNameToFeatureName( requiredPluginName ) );
		} )
		// Since some required plugins do not belong to a feature, we need to remove nulls.
		.filter( Boolean )
		.map( requirement => {
			if ( typeof requirement === 'string' ) {
				return requirement;
			}

			// Ensure that the array requirement does not have duplicates.
			// This can happen when required plugins A and B both end up in the same feature.
			const arrayRequirement = removeDuplicateStrings( requirement as Array<string> );

			// If there is only one option, unwrap the array.
			if ( arrayRequirement.length === 1 ) {
				return arrayRequirement[ 0 ];
			}

			// If the feature requires itself as an alternating requirement, then that requirement is always fulfilled.
			// This can happen when plugin A requiring plugin B both end up in the same feature.
			if ( arrayRequirement.includes( feature.name ) ) {
				return null;
			}

			return arrayRequirement;
		} )
		.filter( ( requirement, i, allRequirements ) => {
			if ( !requirement ) {
				return false;
			}

			// If the requirement is an alternating one, but one of alternating
			// items is included as a regular requirement, then the array is redundant.
			if ( Array.isArray( requirement ) ) {
				return !requirement.some( r => allRequirements.includes( r ) );
			}

			// Requiring oneself is invalid.
			if ( requirement === feature.name ) {
				return false;
			}

			return true;
		} )
		.map( requirement => {
			if ( typeof requirement === 'string' ) {
				return requirement;
			}

			return requirement!.join( ' or ' );
		} );

	return removeDuplicateStrings( requirements as Array<string> );
}
